<template>
  <div class="investigation-wrapper animated fadeIn">
    <div v-if="!connections" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary"></b-spinner>
      <p class="text-muted mt-2">Analyzing interactions...</p>
    </div>

    <div v-else>
      <!-- 1. Summary Cards -->
      <b-row class="mb-4">
        <b-col sm="4">
          <b-card class="text-white bg-primary h-100 border-0 shadow-sm">
            <div class="h4 mb-0">{{ connections.length }}</div>
            <small class="text-uppercase font-weight-bold">Total Partners Identified</small>
            <b-progress class="progress-white progress-xs mt-3" :value="100"/>
          </b-card>
        </b-col>
        <b-col sm="4">
          <b-card class="text-white bg-info h-100 border-0 shadow-sm">
            <div class="h4 mb-0">{{ totalTrades }}</div>
            <small class="text-uppercase font-weight-bold">Total Trades</small>
            <b-progress class="progress-white progress-xs mt-3" :value="100"/>
          </b-card>
        </b-col>
        <b-col sm="4">
          <b-card class="text-white bg-warning h-100 border-0 shadow-sm">
            <div class="h4 mb-0">{{ totalPvP }}</div>
            <small class="text-uppercase font-weight-bold">Ranked PvP Matches</small>
            <b-progress class="progress-white progress-xs mt-3" :value="100"/>
          </b-card>
        </b-col>
      </b-row>

      <!-- 2. Partners Grid -->
      <div class="d-flex justify-content-between align-items-center mb-3 border-bottom-theme pb-2">
        <h5 class="mb-0 text-muted font-weight-bold">Detailed Interaction Analysis</h5>
        <b-button size="sm" variant="outline-info" v-b-modal.investigation-risk-info>
            <i class="fa fa-question-circle"></i> Abuse Scoring
        </b-button>
      </div>

      <b-row>
        <b-col md="6" xl="4" v-for="partner in interactionPartners" :key="partner.id" class="mb-4">
          <b-card no-body class="h-100 shadow-sm bg-dark border-secondary" 
                  :class="{'border-top-danger': partner.abuse_score >= 50, 'opacity-75': partner.banned}">
             <b-card-body>
                <div class="d-flex justify-content-between mb-2">
                    <div>
                        <h5 class="mb-0 font-weight-bold">
                            <router-link :to="'/user/' + partner.username + '?server=' + server" 
                                         :class="partner.banned ? 'text-muted text-decoration-line-through' : 'text-white'">
                                {{ partner.username }}
                            </router-link>
                            <b-badge v-if="partner.banned" variant="danger" class="ml-2 small">
                                {{ partner.ban_status || 'Banned' }}
                            </b-badge>
                        </h5>
                        <small class="text-muted">{{ partner.email }}</small>
                    </div>
                    <div class="text-right">
                        <h3 class="mb-0 font-weight-bold" :class="partner.abuse_score >= 50 ? 'text-danger' : 'text-warning'">{{ partner.abuse_score }}</h3>
                        <small class="text-muted text-uppercase">Score</small>
                    </div>
                </div>
                
                <hr class="border-secondary">

                <!-- Interaction Breakdown -->
                <div class="d-flex justify-content-between align-items-center mb-2" v-if="getMatchCount(partner, 'Trade') > 0">
                    <span class="text-info font-weight-bold"><i class="fa fa-exchange mr-1"></i> Trades</span>
                    <span class="font-weight-bold text-white">{{ getMatchCount(partner, 'Trade') }}</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-2" v-if="getMatchCount(partner, 'PvP') > 0">
                    <span class="text-danger font-weight-bold"><i class="fa fa-trophy mr-1"></i> Ranked PvP</span>
                    <span class="font-weight-bold text-white">{{ getMatchCount(partner, 'PvP') }}</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-2" v-if="getMatchCount(partner, 'Mail') > 0">
                    <span class="text-warning font-weight-bold"><i class="fa fa-envelope mr-1"></i> Mails</span>
                    <span class="font-weight-bold text-white">{{ getMatchCount(partner, 'Mail') }}</span>
                </div>
             </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Investigation Risk Info Modal -->
    <b-modal id="investigation-risk-info" title="Abuse Score Calculation" hide-footer body-class="bg-dark" header-bg-variant="dark" header-text-variant="white">
        <table class="table table-sm table-dark table-striped border-secondary">
            <thead>
                <tr><th>Factor</th><th>Points</th><th>Description</th></tr>
            </thead>
            <tbody>
                <tr><td><b-badge variant="info">Trade</b-badge></td><td>10</td><td>Per interaction (Money/Pokes)</td></tr>
                <tr><td><b-badge variant="warning">Mail</b-badge></td><td>10</td><td>Per mail exchange</td></tr>
                <tr><td><b-badge variant="danger">PvP</b-badge></td><td>5</td><td>Per Ranked match played against</td></tr>
            </tbody>
        </table>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'InvestigationView',
  props: {
    username: { type: String, required: true },
    server: { type: String, required: true }
  },
  data() {
    return {
    }
  },
  computed: {
    ...mapGetters({
      connections: 'investigationConnections'
    }),
    interactionPartners() {
        if (!this.connections) return [];
        return this.connections.filter(c => c.abuse_score > 0).sort((a, b) => b.abuse_score - a.abuse_score);
    },
    totalTrades() {
        return this.interactionPartners.reduce((acc, p) => acc + this.getMatchCount(p, 'Trade'), 0);
    },
    totalPvP() {
        return this.interactionPartners.reduce((acc, p) => acc + this.getMatchCount(p, 'PvP'), 0);
    }
  },
  created() {
      this.fetchData();
  },
  watch: {
    username(newVal, oldVal) {
        if (newVal !== oldVal) {
            this.$store.commit('setInvestigation', null);
            this.fetchData();
        }
    },
    server: 'fetchData'
  },
  methods: {
    fetchData() {
        if (this.username && this.server) {
             this.$store.commit('setInvestigation', null);
             this.emit('getInvestigation', { username: this.username, server: this.server })
        }
    },
    formatMoney(value) { return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "."); },
    getMatchCount(partner, type) {
        const match = partner.matches.find(m => m.type === type);
        return match ? match.count : 0;
    }
  },
  filters: {
      fromNow(val) { return (!val || val.startsWith('0001')) ? '-' : moment(val).fromNow(); },
      formatDate(val) { return (!val || val.startsWith('0001')) ? '-' : moment(val).format('YYYY-MM-DD HH:mm'); }
  }
}
</script>

<style scoped>
.investigation-wrapper { min-height: 400px; }
.bg-dark-subtle { background-color: rgba(0, 0, 0, 0.2); }
.border-bottom-theme { border-bottom: 1px solid #3e515b !important; }
.border-top-danger { border-top: 3px solid #f86c6b !important; }
</style>
